import React, { useState, useEffect } from 'react';
import { Plus, Send, Mail, Download, Eye, FileText, TestTube, CreditCard, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { useToast } from '../../../components/ui/use-toast';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendDialog, setSendDialog] = useState({ open: false, invoice: null, isTest: false });
  const [sending, setSending] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, invoice: null });
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/finance/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ensure we always set an array, even if response is invalid
      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]); // Set empty array on error
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load invoices. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async (invoice, isTest = false) => {
    setSending(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      const endpoint = isTest 
        ? `${API_URL}/api/finance/invoices/${invoice.id}/send-test`
        : `${API_URL}/api/finance/invoices/${invoice.id}/send`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast({
          title: "Success",
          description: isTest 
            ? "Test invoice email sent to Bruno successfully."
            : `Invoice #${invoice.invoice_number} sent successfully.`,
          duration: 5000,
        });
        
        if (!isTest) {
          await fetchInvoices(); // Refresh to update email_sent status
        }
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to send invoice. Please try again.",
        duration: 5000,
      });
    } finally {
      setSending(false);
      setSendDialog({ open: false, invoice: null, isTest: false });
    }
  };

  const handlePreviewPDF = (invoiceId) => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API_URL}/api/finance/invoices/${invoiceId}/pdf/preview?token=${token}`, '_blank');
  };

  const handleDownloadPDF = (invoiceId) => {
    const token = localStorage.getItem('admin_token');
    window.open(`${API_URL}/api/finance/invoices/${invoiceId}/pdf/download?token=${token}`, '_blank');
  };
  
  const handlePayWithStripe = async (invoice) => {
    try {
      const token = localStorage.getItem('admin_token');
      const originUrl = window.location.origin;
      
      toast({
        title: "Redirecting to Stripe...",
        description: "Please wait while we set up your payment.",
      });
      
      const response = await axios.post(
        `${API_URL}/api/finance/stripe/create-checkout`,
        null,
        {
          params: {
            invoice_id: invoice.id,
            origin_url: originUrl
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        variant: "destructive",
        title: "Payment Error",
        description: error.response?.data?.detail || "Failed to initiate payment. Please try again.",
      });
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteDialog.invoice) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/finance/invoices/${deleteDialog.invoice.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Invoice deleted",
        description: `Invoice #${deleteDialog.invoice.invoice_number} was removed.`,
      });
      setDeleteDialog({ open: false, invoice: null });
      await fetchInvoices();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error.response?.data?.detail || "Could not delete invoice.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (invoice) => {
    const status = invoice.status || 'draft';
    const statusConfig = {
      draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800 border-gray-300' },
      sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800 border-blue-300' },
      paid: { label: 'Paid', color: 'bg-green-100 text-green-800 border-green-300' },
      overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800 border-red-300' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={`${config.color} border font-medium`}>{config.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">Create and manage invoices</p>
        </div>
        <Button 
          className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
          onClick={() => navigate('/admin/finance/invoices/create')}
          data-testid="create-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle>All Invoices</CardTitle>
          <CardDescription>View, send, and manage invoices</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00B4D8] mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading invoices...</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No invoices yet</h3>
              <p className="text-gray-600 mb-4">Create your first invoice to get started</p>
              <Button
                className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                onClick={() => navigate('/admin/finance/invoices/create')}
                data-testid="empty-state-create-invoice-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#023E8A]">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Invoice #</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Issuer</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Client</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Issue Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice, index) => (
                    <tr 
                      key={invoice.id}
                      className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      }`}
                      onClick={() => navigate(`/admin/finance/invoices/${invoice.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#023E8A]">
                        #{invoice.invoice_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invoice.company_snapshot?.trading_name || invoice.company_snapshot?.legal_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.client_name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {(invoice.currency || 'AED').toUpperCase()} {Number(invoice.total || invoice.grand_total || invoice.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(invoice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreviewPDF(invoice.id)}
                            className="text-[#00B4D8] hover:text-[#0096c7] hover:bg-blue-50"
                            title="Preview PDF"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPDF(invoice.id)}
                            className="text-[#023E8A] hover:text-[#012a5e] hover:bg-blue-50"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSendDialog({ open: true, invoice, isTest: true })}
                            className="text-[#6C757D] hover:text-[#495057] hover:bg-gray-100"
                            title="Send Test Email"
                          >
                            <TestTube className="w-4 h-4" />
                          </Button>
                          {invoice.email_sent ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSendDialog({ open: true, invoice, isTest: false })}
                              className="border-[#00B4D8] text-[#00B4D8] hover:bg-blue-50"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Resend
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => setSendDialog({ open: true, invoice, isTest: false })}
                              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Send
                            </Button>
                          )}
                          
                          {/* Pay with Stripe button - only for unpaid invoices */}
                          {invoice.status !== 'paid' && (
                            <Button
                              size="sm"
                              onClick={() => handlePayWithStripe(invoice)}
                              className="bg-[#6772e5] hover:bg-[#5469d4] text-white"
                              title="Pay with Stripe"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay
                            </Button>
                          )}

                          {/* Delete invoice button — blocked for paid invoices */}
                          {invoice.status !== 'paid' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteDialog({ open: true, invoice })}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete invoice"
                              data-testid={`delete-invoice-${invoice.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Invoice Dialog */}
      <Dialog open={sendDialog.open} onOpenChange={(open) => setSendDialog({ ...sendDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#023E8A] text-xl font-semibold">
              {sendDialog.isTest ? 'Send Test Invoice Email' : 'Send Invoice'}
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-base leading-relaxed">
              {sendDialog.isTest ? (
                <>
                  A test invoice email will be sent to <strong>Bruno</strong> for visual approval.
                  <span className="block mt-2 text-sm text-[#00B4D8]">
                    This helps validate the email format before sending to clients.
                  </span>
                </>
              ) : (
                <>
                  This will send the invoice email with PDF attachment to the client.
                  {sendDialog.invoice?.email_sent && (
                    <span className="block mt-2 text-sm text-orange-600">
                      This invoice was already sent. Resending will notify the client again.
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {sendDialog.invoice && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 my-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Invoice:</span>
                <span className="font-semibold text-gray-900">#{sendDialog.invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Client:</span>
                <span className="font-semibold text-gray-900">
                  {sendDialog.isTest ? 'bruno.pegado@grupobmp.com' : sendDialog.invoice.client_email}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount:</span>
                <span className="font-semibold text-gray-900">
                  {(sendDialog.invoice.currency || 'USD').toUpperCase()} {Number(sendDialog.invoice.total || sendDialog.invoice.grand_total || sendDialog.invoice.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSendDialog({ open: false, invoice: null, isTest: false })}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleSendInvoice(sendDialog.invoice, sendDialog.isTest)}
              disabled={sending}
              className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  {sendDialog.isTest ? <TestTube className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  {sendDialog.isTest ? 'Send Test Email' : 'Send Invoice'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ open, invoice: open ? deleteDialog.invoice : null })}>
        <DialogContent className="sm:max-w-md" data-testid="delete-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 text-xl font-semibold">
              Delete Invoice
            </DialogTitle>
            <DialogDescription className="text-gray-700 text-base leading-relaxed">
              This will permanently remove the invoice from the active list. Paid invoices cannot be deleted.
            </DialogDescription>
          </DialogHeader>

          {deleteDialog.invoice && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2 my-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Invoice:</span>
                <span className="font-semibold text-gray-900">#{deleteDialog.invoice.invoice_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Client:</span>
                <span className="font-semibold text-gray-900">{deleteDialog.invoice.client_name || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Amount:</span>
                <span className="font-semibold text-gray-900">
                  {(deleteDialog.invoice.currency || 'AED').toUpperCase()} {Number(deleteDialog.invoice.total || deleteDialog.invoice.grand_total || deleteDialog.invoice.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, invoice: null })}
              disabled={deleting}
              data-testid="delete-invoice-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteInvoice}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-invoice-confirm-btn"
            >
              {deleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
